import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Login.css';
import { FirebaseError } from 'firebase/app';

const Login: React.FC = () => {
  const [statusMessage, setStatusMessage] = useState<{ text: string; isSuccess: boolean } | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  const navigate = useNavigate();
  const { loginWithGoogle, loading } = useAuth();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const getFirebaseErrorMessage = (error: FirebaseError): string => {
    switch (error.code) {
      case 'auth/popup-closed-by-user':
        return 'Sign-in popup was closed before completing the sign in';
      case 'auth/cancelled-popup-request':
        return 'The sign-in popup was cancelled';
      case 'auth/popup-blocked':
        return 'Sign-in popup was blocked by browser';
      default:
        return error.message || 'An unknown error occurred';
    }
  };

  const handleGoogleLogin = async () => {
    setStatusMessage({ text: 'Connecting to Google...', isSuccess: false });
    
    try {
      const success = await loginWithGoogle();
      
      if (success) {
        setStatusMessage({ text: 'Authentication successful! Redirecting...', isSuccess: true });
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        setStatusMessage({ text: 'Google sign-in failed', isSuccess: false });
      }
    } catch (error) {
      const errorMessage = error instanceof FirebaseError 
        ? getFirebaseErrorMessage(error) 
        : 'An unexpected error occurred';
        
      setStatusMessage({ text: errorMessage, isSuccess: false });
    }
  };

  return (
    <div className="login-container bg-white text-text-heading">
      <div className="login-main-container flex flex-col md:flex-row">
        {/* Left Section: Branding and Images - Only show on desktop */}
        {!isMobile && (
          <div className="w-full md:w-1/2 branding-section p-8 sm:p-12 lg:p-16 flex flex-col">
            <div>
              {/* Logo */}
              <h1 className="branding-title tracking-wider">SnapMyDesign</h1>
              {/* Tagline */}
              <p className="branding-tagline-small leading-tight">
                Product Photography Made Simple
              </p>
              {/* Image Grid */}
              <div className="image-grid">
                <img 
                  src="/images/login-ui/1.jpg" 
                  alt="Product Photography Example 1" 
                  className="rounded-lg object-cover w-full h-48 sm:h-64 lg:h-80"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://placehold.co/300x400/2d3748/ffffff?text=Image+1';
                  }}
                />
                <img 
                  src="/images/login-ui/2.jpg" 
                  alt="Product Photography Example 2" 
                  className="rounded-lg object-cover w-full h-48 sm:h-64 lg:h-80"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://placehold.co/300x400/2d3748/ffffff?text=Image+2';
                  }}
                />
                <img 
                  src="/images/login-ui/3.jpg" 
                  alt="Product Photography Example 3" 
                  className="rounded-lg object-cover w-full h-48 sm:h-64 lg:h-80"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://placehold.co/300x400/2d3748/ffffff?text=Image+3';
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Right Section: Login Form */}
        <div className={`${isMobile ? 'w-full' : 'w-full md:w-1/2'} form-section p-8 sm:p-12 lg:p-16 flex items-center justify-center`}>
          <div className="max-w-md w-full">
            {/* Header */}
            <h2 className="text-3xl font-bold text-center">
              Welcome to SnapMyDesign
            </h2>
            <p className="text-center text-text-description mt-2">
              Sign in to continue
            </p>

            {/* Google Sign-in */}
            <button 
              className="w-full flex items-center justify-center bg-primary text-white font-semibold py-3 px-4 rounded-lg mt-8 hover:bg-primary-hover transition duration-300 disabled:opacity-70"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              {loading && statusMessage?.text.includes('Google') ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-3"></i>
                  Connecting to Google...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </>
              )}
            </button>

            {/* Footer Text */}
            <p className="text-center text-xs text-text-description mt-8">
              When you sign in, you are agreeing to comply with our{' '}
              <a href="#" className="underline">privacy policy</a> and{' '}
              <a href="#" className="underline">terms & conditions</a>.
            </p>
          </div>
        </div>
      </div>
      
      {statusMessage && (
        <div className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg text-white text-sm z-50 ${
          statusMessage.isSuccess ? 'bg-success-600' : 'bg-secondary-600'
        }`}>
          {statusMessage.text}
        </div>
      )}
    </div>
  );
};

export default Login; 